import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { fmtBRL, fmtCompetencia } from '../../lib/format';
import { Modal } from '../../components/ui/modal';
import { parcelaStatusLabel, parcelaStatusColor } from '../contratos/labels';
import { RefreshCw } from 'lucide-react';

interface Apto {
  cicloId: string; numeroCiclo: number; contratoId: string; numeroContrato: string;
  associadoNome: string; documentoMask: string; agente: string;
  mensalidade: number; parcelasPagas: number; parcelasTotal: number; progresso: string;
}

interface ContratoDetalhe {
  id: string; numeroContrato: string; qtdParcelas: number;
  parcelas: Array<{ id: string; numeroParcela: number; competencia: string; valorPrevisto: number; valorPago: number; status: string; dataPagamento: string | null }>;
}

export function AptosRenovarPage() {
  const [busca, setBusca] = useState('');
  const [verContrato, setVerContrato] = useState<{ id: string; nome: string } | null>(null);

  const { data: contratoDet, isLoading: loadingDet } = useQuery({
    queryKey: ['contrato-aptos', verContrato?.id],
    queryFn: () => api<ContratoDetalhe>(`/contratos/${verContrato!.id}`),
    enabled: !!verContrato,
  });
  const { data, isLoading } = useQuery({
    queryKey: ['aptos-renovar-page'],
    queryFn: () => api<{ total: number; items: Apto[] }>('/relatorios/aptos-renovar'),
  });

  const itens = (data?.items ?? []).filter((c) => {
    if (!busca) return true;
    const b = busca.toLowerCase();
    return (c.associadoNome ?? '').toLowerCase().includes(b)
      || (c.documentoMask ?? '').includes(b)
      || (c.numeroContrato ?? '').toLowerCase().includes(b)
      || (c.agente ?? '').toLowerCase().includes(b);
  });

  const de3 = (data?.items ?? []).filter((c) => c.parcelasTotal === 3).length;
  const de4 = (data?.items ?? []).filter((c) => c.parcelasTotal === 4).length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-primary" /> Aptos a Renovar
        </h1>
        <p className="text-sm text-muted-foreground">Ciclos no ponto de renovação (2/3 ou 3/4)</p>
      </div>

      <div className="mb-4 grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total aptos</p><p className="text-2xl font-bold">{data?.total ?? '—'}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Combos de 3 (2/3)</p><p className="text-2xl font-bold">{de3}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Combos de 4 (3/4)</p><p className="text-2xl font-bold">{de4}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Lista de aptos
            <Input placeholder="Buscar por nome, CPF, contrato ou agente…" value={busca} onChange={(e) => setBusca(e.target.value)} className="mt-2 w-full max-w-md font-normal" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? <div className="flex justify-center p-8"><Spinner /></div> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-gray-500">
                <th className="py-2">Associado</th><th className="py-2">Contrato</th>
                <th className="py-2 text-center">Progresso</th>
                <th className="py-2 text-right pr-6">Mensalidade</th><th className="py-2">Agente</th>
              </tr></thead>
              <tbody>
                {itens.map((c) => (
                  <tr key={c.cicloId} className="border-b">
                    <td className="py-2">
                      <button className="text-primary hover:underline text-left font-medium"
                        onClick={() => setVerContrato({ id: c.contratoId, nome: c.associadoNome })}>
                        {c.associadoNome}
                      </button>
                      <br /><span className="text-xs text-gray-500">{c.documentoMask}</span>
                    </td>
                    <td className="py-2"><Link to={`/contratos/${c.contratoId}`} className="text-primary hover:underline text-xs">{c.numeroContrato}</Link></td>
                    <td className="py-2 text-center font-semibold">{c.progresso}</td>
                    <td className="py-2 text-right pr-6">{fmtBRL(c.mensalidade)}</td>
                    <td className="py-2 text-xs">{c.agente}</td>
                  </tr>
                ))}
                {itens.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-gray-400">Nenhum ciclo apto encontrado.</td></tr>}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {verContrato && (
        <Modal open onClose={() => setVerContrato(null)} title={`Histórico de descontos — ${verContrato.nome}`}>
          {loadingDet ? <div className="flex justify-center p-6"><Spinner /></div> : contratoDet ? (
            <div className="space-y-3">
              {(() => {
                const pagas = contratoDet.parcelas.filter((p) => p.status === 'paga').length;
                return (
                  <p className="text-sm">
                    <strong>{pagas}</strong> desconto(s) efetivo(s) de {contratoDet.qtdParcelas} no contrato.
                    <span className="text-muted-foreground"> O ciclo conta os descontos que entraram na folha.</span>
                  </p>
                );
              })()}
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-gray-500">
                  <th className="py-1.5">Competência</th><th className="py-1.5 text-right">Previsto</th>
                  <th className="py-1.5 text-right">Descontado</th><th className="py-1.5 text-center">Status</th>
                </tr></thead>
                <tbody>
                  {contratoDet.parcelas.map((p) => (
                    <tr key={p.id} className="border-b">
                      <td className="py-1.5">{fmtCompetencia(p.competencia)}</td>
                      <td className="py-1.5 text-right">{fmtBRL(p.valorPrevisto)}</td>
                      <td className="py-1.5 text-right">{p.valorPago > 0 ? fmtBRL(p.valorPago) : '—'}</td>
                      <td className="py-1.5 text-center">
                        <span className={`text-xs px-2 py-0.5 rounded ${parcelaStatusColor[p.status] ?? 'bg-gray-100'}`}>
                          {parcelaStatusLabel[p.status] ?? p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="pt-2 text-right">
                <Link to={`/contratos/${verContrato.id}`} className="text-primary hover:underline text-sm">
                  Ver detalhe completo →
                </Link>
              </div>
            </div>
          ) : <p className="text-sm text-gray-500">Não foi possível carregar.</p>}
        </Modal>
      )}
    </div>
  );
}
